/**
 * iter87.262 — usePlayoffSeriesIntel hook.
 *
 * Fetches the current-round Playoff Series Intel doc for an MLB/NBA/
 * NWSL/NFL/CFB Playoffs tournament. Silently handles 402 (Free tier)
 * and 404 (feature flag off / not a playoff-series template) — in
 * both cases the hook returns `{ intel: null, visible: false }`,
 * which TodoChips reads to mean "hide the chip".
 *
 * iter87.465.26 (user-requested — "there should be 2 intel tiles...
 * Series Intel should only be for the series predictor games (not the
 * wild card playin)... playoff intel... would follow the NFL/CFB
 * rivalry intel shape for the knockout stage") — MLS runs TWO
 * independent shapes in one tournament (best-of-3 Round One series vs
 * single-game Conf Semis+ knockout), so it fetches two separately
 * scoped docs (`seriesIntel`/`knockoutIntel`) instead of one rotating
 * "current round" doc. Every other sport keeps the single legacy
 * `intel`/`hasData`/`visible` shape, unchanged.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const ELIGIBLE_TEMPLATES = ["mlb_playoffs_api", "nba_playoffs_api", "nwsl_playoffs_api", "nfl_playoffs_api", "cfb_playoffs_api", "mls_playoffs_api", "ucl_playoffs_api"];

export function usePlayoffSeriesIntel(tournament) {
    const tournamentId = tournament?.id;
    const isMls = tournament?.template === "mls_playoffs_api";
    const isUcl = tournament?.template === "ucl_playoffs_api";
    const eligible = ELIGIBLE_TEMPLATES.includes(tournament?.template);
    const [intel, setIntel] = useState(null);
    const [seriesIntel, setSeriesIntel] = useState(null);
    const [knockoutIntel, setKnockoutIntel] = useState(null);
    const [uclTwoLegIntel, setUclTwoLegIntel] = useState(null);
    const [uclFinalIntel, setUclFinalIntel] = useState(null);
    const [ready, setReady] = useState(false);

    const load = useCallback(async () => {
        if (!eligible || !tournamentId) {
            setIntel(null); setSeriesIntel(null); setKnockoutIntel(null);
            setUclTwoLegIntel(null); setUclFinalIntel(null);
            setReady(true);
            return;
        }
        try {
            if (isMls) {
                const [s, k] = await Promise.all([
                    api.getPlayoffSeriesIntel(tournamentId, "series").catch(() => null),
                    api.getPlayoffSeriesIntel(tournamentId, "knockout").catch(() => null),
                ]);
                setSeriesIntel(s);
                setKnockoutIntel(k);
                setIntel(null);
                setUclTwoLegIntel(null); setUclFinalIntel(null);
            } else if (isUcl) {
                const [tl, fi] = await Promise.all([
                    api.getPlayoffSeriesIntel(tournamentId, "two_leg").catch(() => null),
                    api.getPlayoffSeriesIntel(tournamentId, "final").catch(() => null),
                ]);
                setUclTwoLegIntel(tl);
                setUclFinalIntel(fi);
                setIntel(null);
                setSeriesIntel(null); setKnockoutIntel(null);
            } else {
                const data = await api.getPlayoffSeriesIntel(tournamentId);
                setIntel(data);
                setSeriesIntel(null);
                setKnockoutIntel(null);
                setUclTwoLegIntel(null); setUclFinalIntel(null);
            }
        } catch (e) {
            // 402/404/5xx: silently hide.
            setIntel(null); setSeriesIntel(null); setKnockoutIntel(null);
            setUclTwoLegIntel(null); setUclFinalIntel(null);
        } finally {
            setReady(true);
        }
    }, [tournamentId, eligible, isMls, isUcl]);

    useEffect(() => { load(); }, [load]);

    const hasData = !!intel && Array.isArray(intel.series) && intel.series.length > 0;
    // iter87.364 (user-reported) — visible as soon as the tournament
    // is eligible+entitled (a successful fetch, even with zero series
    // yet), not only once real data exists — see TodoChips.jsx item 9
    // for the placeholder copy shown while `hasData` is false.
    const visible = !!intel;
    const seriesHasData = !!seriesIntel && Array.isArray(seriesIntel.series) && seriesIntel.series.length > 0;
    const knockoutHasData = !!knockoutIntel && Array.isArray(knockoutIntel.series) && knockoutIntel.series.length > 0;
    const uclTwoLegHasData = !!uclTwoLegIntel && Array.isArray(uclTwoLegIntel.series) && uclTwoLegIntel.series.length > 0;
    const uclFinalHasData = !!uclFinalIntel && Array.isArray(uclFinalIntel.series) && uclFinalIntel.series.length > 0;
    return {
        intel, hasData, visible, ready, refresh: load,
        // MLS-only — null for every other sport.
        seriesIntel: isMls ? { intel: seriesIntel, hasData: seriesHasData, visible: !!seriesIntel } : null,
        knockoutIntel: isMls ? { intel: knockoutIntel, hasData: knockoutHasData, visible: !!knockoutIntel } : null,
        // UCL-only — null for every other sport.
        uclTwoLegIntel: isUcl ? { intel: uclTwoLegIntel, hasData: uclTwoLegHasData, visible: !!uclTwoLegIntel } : null,
        uclFinalIntel: isUcl ? { intel: uclFinalIntel, hasData: uclFinalHasData, visible: !!uclFinalIntel } : null,
    };
}
